import { memo } from 'react'
import { Button, Popover } from '@highlight/ui'
import { Bug } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { isMac } from '@shared/utils/platform'

export type BetaPromptSide = 'top' | 'bottom' | 'left' | 'right'
export type BetaPromptAlign = 'start' | 'center' | 'end'

interface BetaPromptProps {
  side?: BetaPromptSide
  align?: BetaPromptAlign
  className?: string
  /** Omitted on surfaces that can't reach the support widget; the copy stands alone. */
  onSendFeedback?: () => void
}

/** Split from the trigger so a surface with its own anchor can reuse the copy. */
export const BetaPrompt = memo(({ side = 'top', align = 'center', className, onSendFeedback }: BetaPromptProps) => {
  return (
    <Popover.Content
      side={side}
      align={align}
      sideOffset={8}
      // No arrow. It mounts inside the popup rather than the positioner, so it
      // keeps its own fill and position while the popup shifts off a collision
      // boundary, and over the composer it reads as a separate tab with the
      // popup's bottom border cutting across it.
      className={twMerge('flex w-[300px] flex-col gap-3 p-4', className)}
    >
      {/* Popover.Title and Popover.Description are hardcoded to white, which
          vanishes against the popup background in the light theme. */}
      <div className="flex flex-col gap-1.5">
        <Popover.Title className="text-foreground">Highlight for {isMac ? 'Mac' : 'Windows'} is in beta</Popover.Title>
        <Popover.Description className="text-foreground-secondary">
          {onSendFeedback
            ? 'Things may still be rough around the edges. We’d love to hear what’s working and what isn’t.'
            : 'Things may still be rough around the edges while we polish it.'}
        </Popover.Description>
      </div>
      {onSendFeedback && (
        <Button variant="primary" left={<Bug aria-hidden="true" />} className="w-full" onClick={onSendFeedback}>
          Send feedback
        </Button>
      )}
    </Popover.Content>
  )
})
BetaPrompt.displayName = 'BetaPrompt'
